import { useState, useEffect } from 'react';
import { API_BASE } from '../utils/constants';

export const useEmployees = (user) => {
  const [employees, setEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/employees`, {
          headers: {
            "Content-Type": "application/json",
            "X-User-Email": user?.email || ""
          },
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch employees: ${res.status}`);
        }
        const data = await res.json();
        setEmployees(data.employees || {});
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      fetchEmployees();
    }
  }, [user]);

  return { employees, setEmployees, loading, error };
};

export const usePolicies = (user, activeTab) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/admin/pto_policies`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': user?.email || ''
          },
          credentials: 'include'
        });
        if (!res.ok) throw new Error(`Failed to fetch policies: ${res.status}`);
        const data = await res.json();
        setPolicies(data.policies || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    // Always fetch policies if user is available and we don't have policies yet
    if (user?.email && policies.length === 0) {
      fetchPolicies();
    }
  }, [user, policies.length]);

  return { policies, setPolicies, loading };
};

export const useRequests = (user, activeTab) => {
  const [requests, setRequests] = useState([]);
  const [upcomingRequests, setUpcomingRequests] = useState([]);
  const [pastRequests, setPastRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'requests') return;
    
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/admin/pto_requests`, {
          headers: { 'Content-Type': 'application/json', 'X-User-Email': user?.email || '' },
          credentials: 'include'
        });
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = await res.json();
        
        // Set all requests (for backwards compatibility)
        const sorted = (data.requests || []).sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        setRequests(sorted);
        
        // Use the upcoming/past structure from API
        if (data.upcoming && data.past) {
          setUpcomingRequests(data.upcoming);
          setPastRequests(data.past);
        } else {
          // Fallback: separate requests manually if API doesn't provide the split
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const upcoming = [];
          const past = [];
          
          sorted.forEach(req => {
            try {
              const startDate = new Date(req.start_date);
              startDate.setHours(0, 0, 0, 0);
              
              if (startDate >= today) {
                upcoming.push(req);
              } else {
                past.push(req);
              }
            } catch {
              past.push(req);
            }
          });
          
          setUpcomingRequests(upcoming);
          setPastRequests(past);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [activeTab, user]);

  return { 
    requests, 
    setRequests, 
    upcomingRequests, 
    setUpcomingRequests, 
    pastRequests, 
    setPastRequests, 
    loading 
  };
};

export const useReports = (user, activeTab) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'reports' || !user?.email) return;
    
    const fetchReports = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/admin/pto_reports`, {
          headers: { 
            'Content-Type': 'application/json', 
            'X-User-Email': user?.email || '' 
          },
          credentials: 'include'
        });
        
        if (!res.ok) {
          throw new Error(`Failed: ${res.status}`);
        }
        
        const data = await res.json();
        setReports(data.reports || []);
      } catch (e) {
        console.error('Error fetching PTO reports:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [activeTab, user]);

  return { reports, setReports, loading };
};

export const useAdjustments = (user, activeTab) => {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'adjustments') return;
    
    const fetchAdjustments = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/admin/balance_adjustments`, {
          headers: { 'Content-Type': 'application/json', 'X-User-Email': user?.email || '' },
          credentials: 'include'
        });
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = await res.json();
        setAdjustments(data.adjustments || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchAdjustments();
  }, [activeTab, user]);

  return { adjustments, setAdjustments, loading };
};


export const useDigest = (user, activeTab, digestWeekStart) => {
  const [digestText, setDigestText] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchDigest = async (weekStart = null) => {
    if (!user?.email) return;
    
    try {
      setLoading(true);
      const url = weekStart 
        ? `${API_BASE}/api/admin/pto_digest?start=${weekStart}`
        : `${API_BASE}/api/admin/pto_digest`;
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json', 'X-User-Email': user.email },
        credentials: 'include'
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = await res.json();
      setDigestText(data.digest || '');
    } catch (e) {
      console.error('Error fetching digest:', e);
      setDigestText('Error loading digest');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'digest') return;
    fetchDigest(digestWeekStart);
  }, [activeTab, digestWeekStart, user]);

  return { digestText, setDigestText, loading, fetchDigest };
}; 